import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, RotateCcw, Save, GripVertical } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const uid = (p) => `${p}_${Math.random().toString(36).slice(2, 8)}`;

export default function KpiTemplateEditor() {
  const { user } = useAuth();
  const [tpl, setTpl] = useState(null);
  const [scope, setScope] = useState("user");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/kpi/template");
    setTpl(JSON.parse(JSON.stringify(data)));
  };
  useEffect(() => { load(); }, []);

  const addSection = () => {
    setTpl((t) => ({
      ...t,
      sections: [
        ...t.sections,
        { id: uid("sec"), title: "New Section", metrics: [{ id: uid("m"), label: "New Metric", weekly_target: 0 }] },
      ],
    }));
  };
  const removeSection = (sid) => setTpl((t) => ({ ...t, sections: t.sections.filter((s) => s.id !== sid) }));

  const updateSection = (sid, patch) => setTpl((t) => ({
    ...t, sections: t.sections.map((s) => (s.id === sid ? { ...s, ...patch } : s)),
  }));

  const addMetric = (sid) => setTpl((t) => ({
    ...t, sections: t.sections.map((s) =>
      s.id === sid ? { ...s, metrics: [...s.metrics, { id: uid("m"), label: "New Metric", weekly_target: 0 }] } : s
    ),
  }));

  const updateMetric = (sid, mid, patch) => setTpl((t) => ({
    ...t, sections: t.sections.map((s) =>
      s.id === sid ? { ...s, metrics: s.metrics.map((m) => (m.id === mid ? { ...m, ...patch } : m)) } : s
    ),
  }));

  const removeMetric = (sid, mid) => setTpl((t) => ({
    ...t, sections: t.sections.map((s) =>
      s.id === sid ? { ...s, metrics: s.metrics.filter((m) => m.id !== mid) } : s
    ),
  }));

  const toggleColumn = (col) => setTpl((t) => {
    const cur = t.track_columns || [];
    return { ...t, track_columns: cur.includes(col) ? cur.filter((c) => c !== col) : [...cur, col] };
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/kpi/template", {
        name: tpl.name, description: tpl.description,
        track_columns: tpl.track_columns, track_income: tpl.track_income,
        sections: tpl.sections, scope,
      });
      toast.success(`Template saved (${scope})`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!confirm("Reset to the original Wholesaling KPI template? Your custom changes will be lost.")) return;
    await api.post(`/kpi/template/reset?scope=${scope}`);
    toast.success("Reset to original template");
    load();
  };

  if (!tpl) return <div className="p-10 text-neutral-500">Loading…</div>;

  const ALL_COLS = ["actual", "answers", "leads", "offers", "deals"];

  return (
    <div className="p-8 md:p-10 max-w-[1300px]">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#39FF14] mb-2">// KPI template</div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight">
            Design your <span className="text-[#39FF14]">KPI strategy</span>.
          </h1>
          <p className="text-sm text-neutral-400 mt-3 max-w-2xl">
            The default is the original <em>Wholesaling KPI Tracker Template</em>. Add or rename sections/metrics,
            adjust weekly targets, and pick which columns to track. Team Leaders can save a template for the entire team.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="reset-template-btn"
            onClick={reset}
            variant="outline"
            className="rounded-none hairline bg-transparent hover:bg-[#111] hover:text-red-400 text-xs uppercase tracking-widest h-10"
          >
            <RotateCcw size={14} className="mr-2" /> Reset to original
          </Button>
          <Button
            data-testid="save-template-btn"
            onClick={save}
            disabled={saving}
            className="rounded-none bg-[#39FF14] text-black hover:bg-white text-xs uppercase tracking-widest h-10 px-6"
          >
            <Save size={14} className="mr-2" /> {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      <div className="hairline bg-[#0F0F0F] p-6 mb-3">
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1.5">Template name</label>
            <Input
              data-testid="template-name-input"
              value={tpl.name || ""}
              onChange={(e) => setTpl({ ...tpl, name: e.target.value })}
              className="rounded-none bg-[#0A0A0A] hairline"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1.5">Save scope</label>
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger data-testid="scope-select" className="rounded-none bg-[#0A0A0A] hairline">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none bg-[#0F0F0F] hairline">
                <SelectItem value="user">My template (personal)</SelectItem>
                <SelectItem value="team" disabled={!user?.team_id || user?.role !== "team_leader"}>
                  Team template (leaders only)
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1.5">Description</label>
            <Input
              data-testid="template-desc-input"
              value={tpl.description || ""}
              onChange={(e) => setTpl({ ...tpl, description: e.target.value })}
              className="rounded-none bg-[#0A0A0A] hairline"
            />
          </div>
        </div>

        <div className="mt-5">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Track columns</div>
          <div className="flex flex-wrap gap-2">
            {ALL_COLS.map((c) => {
              const on = (tpl.track_columns || []).includes(c);
              return (
                <button
                  key={c}
                  data-testid={`toggle-col-${c}`}
                  onClick={() => toggleColumn(c)}
                  className={`hairline px-3 py-1.5 text-[10px] uppercase tracking-widest transition-colors ${
                    on ? "bg-[#39FF14] text-black border-[#39FF14]" : "bg-transparent text-neutral-400 hover:text-white"
                  }`}
                >
                  {c}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {tpl.sections.map((sec, si) => (
        <div key={sec.id} data-testid={`edit-section-${sec.id}`} className="hairline bg-[#0F0F0F] mb-3">
          <div className="p-5 border-b border-neutral-900 flex items-center gap-3">
            <GripVertical size={16} className="text-neutral-600" />
            <Input
              data-testid={`section-title-${si}`}
              value={sec.title}
              onChange={(e) => updateSection(sec.id, { title: e.target.value })}
              className="rounded-none bg-[#0A0A0A] hairline font-display font-bold text-lg flex-1 max-w-md"
            />
            <Button
              data-testid={`del-section-${si}`}
              onClick={() => removeSection(sec.id)}
              variant="outline"
              className="rounded-none hairline bg-transparent hover:bg-[#1a0000] hover:text-red-400 h-9 text-xs uppercase tracking-widest"
            >
              <Trash2 size={14} />
            </Button>
          </div>
          <div className="divide-y divide-neutral-900">
            {sec.metrics.map((m, mi) => (
              <div key={m.id} className="grid grid-cols-12 gap-3 items-center px-5 py-3">
                <Input
                  data-testid={`metric-label-${si}-${mi}`}
                  value={m.label}
                  onChange={(e) => updateMetric(sec.id, m.id, { label: e.target.value })}
                  placeholder="Metric name"
                  className="col-span-7 rounded-none bg-[#0A0A0A] hairline"
                />
                <div className="col-span-4 flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-widest text-neutral-500 shrink-0">Weekly tgt</span>
                  <Input
                    type="number"
                    min="0"
                    data-testid={`metric-target-${si}-${mi}`}
                    value={m.weekly_target ?? 0}
                    onChange={(e) => updateMetric(sec.id, m.id, { weekly_target: Number(e.target.value) })}
                    className="rounded-none bg-[#0A0A0A] hairline text-right tabular"
                  />
                </div>
                <Button
                  data-testid={`del-metric-${si}-${mi}`}
                  onClick={() => removeMetric(sec.id, m.id)}
                  variant="outline"
                  className="col-span-1 rounded-none hairline bg-transparent hover:text-red-400 h-9"
                >
                  <Trash2 size={12} />
                </Button>
              </div>
            ))}
            <div className="px-5 py-3">
              <button
                data-testid={`add-metric-${si}`}
                onClick={() => addMetric(sec.id)}
                className="text-[10px] uppercase tracking-widest text-neutral-500 hover:text-[#39FF14] flex items-center gap-1"
              >
                <Plus size={12} /> Add metric
              </button>
            </div>
          </div>
        </div>
      ))}

      <Button
        data-testid="add-section-btn"
        onClick={addSection}
        variant="outline"
        className="rounded-none hairline bg-transparent hover:bg-[#111] hover:text-[#39FF14] w-full h-12 text-xs uppercase tracking-widest"
      >
        <Plus size={14} className="mr-2" /> Add section
      </Button>
    </div>
  );
}
